import React, { useCallback, useState } from 'react'
import { Modal } from 'is24-corecss'
import { useTranslation } from 'react-i18next'
import { AiUploadImageSection } from './AiUploadImageSection'
import { AiUploadDocumentSection } from './AiUploadDocumentSection'
import { AiDataExtractionAnalyzingModal, ProcessingState } from './AiDataExtractionAnalyzingModal'
import { useAiExtractionFlow } from './useAiExtractionFlow'
import { useAiUploadedAttachments } from './useAiUploadedAttachments'
import { AiDataExtractionUploadError } from './AiDataExtractionUploadError'

interface AiDataExtractionUploadModalProps {
  extractionApiError?: boolean
  isOpen: boolean
  onClearExtractionApiError?: () => void
  onClose: () => void
  onExtractionFailed?: () => void
  onSuccess?: () => void
}

export const AiDataExtractionUploadModal = ({
  extractionApiError,
  isOpen,
  onClearExtractionApiError,
  onClose,
  onExtractionFailed,
  onSuccess,
}: AiDataExtractionUploadModalProps) => {
  const { t } = useTranslation()
  const isExtractionErrorControlled = extractionApiError !== undefined
  const [internalExtractionApiError, setInternalExtractionApiError] = useState(false)
  const showExtractionApiError = isExtractionErrorControlled ? extractionApiError : internalExtractionApiError

  const clearExtractionApiError = useCallback(() => {
    if (isExtractionErrorControlled) {
      onClearExtractionApiError?.()
    } else {
      setInternalExtractionApiError(false)
    }
  }, [isExtractionErrorControlled, onClearExtractionApiError])

  const handleModalClose = useCallback(() => {
    if (!isExtractionErrorControlled) {
      setInternalExtractionApiError(false)
    }
    onClose()
  }, [isExtractionErrorControlled, onClose])

  const {
    documentsMaxFileSizeErrorText,
    energyCertificateDocument,
    energyCertificateErrorText,
    floorPlanDocument,
    floorPlanErrorText,
    getLatestAttachmentsForExtraction,
    hasSelectedFiles,
    imageErrorText,
    images,
    onEnergyCertificateRemove,
    onEnergyCertificateSelect,
    onFloorPlanRemove,
    onFloorPlanSelect,
    onImageRemove,
    onImageReorder,
    onImageSelect,
    waitForPendingUploads,
  } = useAiUploadedAttachments()
  const { isAnalyzingOpen, processingState, retryAnalyzing, startAnalyzing } = useAiExtractionFlow({
    getLatestAttachmentsForExtraction,
    hasSelectedFiles,
    onClearExtractionApiError: clearExtractionApiError,
    onClose,
    onExtractionFailed: () => {
      if (isExtractionErrorControlled) {
        onExtractionFailed?.()
      } else {
        setInternalExtractionApiError(true)
      }
    },
    onSuccess,
    waitForPendingUploads,
  })

  const primaryFooterBtn = (
    <Modal.FooterPrimaryBtn
      label={t('wizard.uploadAiBanner.modal.uploadButton')}
      onClick={startAnalyzing}
      disabled={!hasSelectedFiles}
      variant='filledDefault'
    />
  )
  const secondaryFooterBtn = (
    <Modal.FooterSecondaryBtn label={t('wizard.uploadAiBanner.modal.cancelButton')} onClick={handleModalClose} />
  )

  return (
    <>
      <Modal
        visible={isOpen && !isAnalyzingOpen}
        ariaLabelCloseButton='close'
        title={t('wizard.uploadAiBanner.modal.title')}
        onClose={handleModalClose}
        secondaryFooterBtn={secondaryFooterBtn}
        primaryFooterBtn={primaryFooterBtn}
        width={601}
      >
        <p className='font-body-large-regular margin-bottom-l'>{t('wizard.uploadAiBanner.modal.description')}</p>
        {showExtractionApiError ? (
          <AiDataExtractionUploadError
            className='margin-bottom-l'
            data-testid='ai-extraction-api-error'
            message={t('wizard.uploadAiBanner.modal.extractionApiError')}
            role='alert'
          />
        ) : null}
        <AiUploadImageSection
          errorText={imageErrorText}
          images={images}
          onFileSelect={onImageSelect}
          onRemove={onImageRemove}
          onReorder={onImageReorder}
        />
        <AiUploadDocumentSection
          documentsSectionErrorText={documentsMaxFileSizeErrorText}
          energyCertificateErrorText={energyCertificateErrorText}
          floorPlanDocument={floorPlanDocument}
          floorPlanErrorText={floorPlanErrorText}
          energyCertificateDocument={energyCertificateDocument}
          onFloorPlanSelect={onFloorPlanSelect}
          onEnergyCertificateSelect={onEnergyCertificateSelect}
          onFloorPlanRemove={onFloorPlanRemove}
          onEnergyCertificateRemove={onEnergyCertificateRemove}
        />
        <p className='font-body-small-regular margin-bottom-l margin-top-m font-light'>
          <span className={'font-body-small-bold'}>{t('wizard.uploadAiBanner.modal.hint')}</span>
          <span>{t('wizard.uploadAiBanner.modal.hintText')}</span>
        </p>
      </Modal>
      <AiDataExtractionAnalyzingModal
        visible={isAnalyzingOpen}
        state={processingState}
        onRetry={processingState === ProcessingState.Error ? retryAnalyzing : undefined}
      />
    </>
  )
}
